import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";

// Utils
import constants from "#src/config/constants";

// API related
import { IEndpointDevice } from "#src/services/endpoint/devices/types";
import { endpointService } from "#src/services/endpoint";
import useFetchPaginated from "#src/hooks/useFetchPaginated";

// Components
import { IFilterItemV2 } from "#src/commonV2/Table/Filter/types";

// Children
import { PolicyTable } from "#src/components/endpoint/devices/details/policy/PolicyTable";
import {
  DeviceDetailsPolicySearchParamKey,
  PolicyFilter,
} from "#src/components/endpoint/devices/details/policy/PolicyFilter";
import { useFilter } from "#src/commonV2/Table/Filter/useFilter";
import { useTranslation } from "react-i18next";
import { PolicyComplianceValue } from "#src/services/endpoint/devices/enums";

const HostPolicies = () => {
  const { t } = useTranslation("endpoint", { keyPrefix: "devices" });

  const { hostDetails, workspaceId } = useOutletContext<{
    hostDetails: IEndpointDevice;
    workspaceId: string;
    hostId: string;
  }>();

  if (!hostDetails) return null;

  const complianceItems: IFilterItemV2[] = useMemo(
    () => [
      {
        key: PolicyComplianceValue.PASSED,
        value: PolicyComplianceValue.PASSED,
        getLabel: () => t("policies.compliance.passed"),
        leftIcon: (
          <span className="h-2 w-2 rounded-full bg-success-v2-500"></span>
        ),
      },
      {
        key: PolicyComplianceValue.FAILED,
        value: PolicyComplianceValue.FAILED,
        getLabel: () => t("policies.compliance.failed"),
        leftIcon: (
          <span className="h-2 w-2 rounded-full bg-error-v2-500"></span>
        ),
      },
    ],
    [t]
  );

  // ----------------------------- DATA -----------------------------

  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(constants.DEFAULT_PAGE_SIZE);

  const {
    renderProps: complianceTypeFilterRenderProps,
    value: complianceType,
  } = useFilter({
    label: t("policies.compliance.label"),
    selectionList: complianceItems,
    options: {
      searchParamKey: DeviceDetailsPolicySearchParamKey.COMPLIANCE,
    },
  });

  // --------------- COMPUTED ---------------

  const fetchParams = useMemo<
    Parameters<typeof endpointService.listDevicePolicies>
  >(
    () => [
      workspaceId,
      hostDetails.id,
      {
        policy_compliance_status: complianceType,
        q: searchKeyword,
        page: selectedPage,
        size: pageSize,
      },
    ],
    [
      workspaceId,
      hostDetails,
      complianceType,
      searchKeyword,
      selectedPage,
      pageSize,
    ]
  );

  // --------------- HOOKS ---------------

  const {
    list: hostPolicyList,
    count: hostPolicyCount,
    isLoading,
  } = useFetchPaginated(endpointService.listDevicePolicies, fetchParams);

  // --------------- RENDER ---------------

  return (
    <>
      {/* Filter */}
      <PolicyFilter
        searchKeyword={searchKeyword}
        setSearchKeyword={setSearchKeyword}
        setSelectedPage={setSelectedPage}
        complianceTypeFilterRenderProps={complianceTypeFilterRenderProps}
      />
      {/* Filter end */}

      {/* Table */}
      <PolicyTable
        hostPolicyList={hostPolicyList || []}
        isLoading={isLoading}
        hostPolicyCount={hostPolicyCount}
        selectedPage={selectedPage}
        setSelectedPage={setSelectedPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
      {/* Table end */}
    </>
  );
};

export default HostPolicies;
